import { FurniViewer } from "@/gui/components/furni-viewer";
import { RoomTilemap } from "@/utils/tilemap";
import { FigureManager } from "@/managers/figure-manager";
import { FurnitureManager } from "@/managers/furniture-manager";
import { Actor, Color, Scene, Vector, vec } from "excalibur";
import { Chatbox } from "@/gui/components/chat-box";
import { loadedAssets } from "@/loader";
import { tileToWorld } from "@/utils/tile";
import { render } from "lit-html";
import { BuildMode } from "@/gui/components/build-mode";
import { loadImageResource, loadResource } from "@/utils/resource";
import { centerCameraStrategy } from "@/camera-strategy";
import { LocationInfo } from "@/gui/components/location-info";
import { normalizeAttribute, parseSpriteDescription } from "@/utils/character";
import { UserViewer } from "@/gui/components/user-viewer";
import gameApp from "..";
import { NPCManager } from "@/managers/npc-manager";

export default class Location extends Scene {
  furniViewer: FurniViewer;
  figureManager: FigureManager;
  roomTileMap: RoomTilemap;
  furnitureManager: FurnitureManager;
  chatbox: any;
  player: any;
  preloadFurnitures: any;
  tilemap: number[][];
  public locationInfo: LocationInfo;
  userViewer: UserViewer;
  ownerId: number;
  npcManager: NPCManager;

  constructor({
    tilemap,
    preloadFurnitures,
    ownerId,
  }: {
    tilemap: number[][];
    preloadFurnitures: any;
    ownerId: number;
  }) {
    super();
    this.tilemap = tilemap;
    this.ownerId = ownerId;
    this.preloadFurnitures = preloadFurnitures;
  }

  public onInitialize() {
    this.figureManager = new FigureManager();
    this.furnitureManager = new FurnitureManager(this.engine.currentScene);

    this.createTilemap();
    this.addFurnituresToRoom(this.preloadFurnitures.furnitures);
    this.furnitureManager = new FurnitureManager(this.engine.currentScene);
    this.displayGameUI();
    this.setEventListners();

    this.npcManager = new NPCManager(this, this.roomTileMap.tileMap.pos);
  }

  public update(engine: ex.Engine, delta: number) {
    super.update(engine, delta);

    if (this.npcManager) {
      this.npcManager.updateNPCMovements(delta);
    }
  }

  public createTilemap() {
    this.roomTileMap = new RoomTilemap(this.engine, this.tilemap);
    this.roomTileMap.addToScene(this.engine.currentScene);
  }

  public setCameraPositionToPlayer(player: any): void {
    const cameraOffset = 20;
    this.camera.addStrategy(
      centerCameraStrategy(
        this.camera,
        player,
        cameraOffset,
        this.roomTileMap.getFullDimensions().width
      )
    );
    const playerPosition = player;
    playerPosition.pos = this.roomTileMap.tileMap.pos.add(
      tileToWorld(vec(player.x, player.y))
    );

    const roomDimensions = this.roomTileMap.getFullDimensions();
    if (roomDimensions.width <= window.innerWidth) return;

    this.camera.x = player.center.x;
    this.camera.y = player.center.y;
  }

  public loadLookResources(users: any) {
    const looks = users.map((user) => user.look);

    // Collect all promises for resource loading
    const resourcePromises = looks.flatMap((look) => {
      const parsedLook = parseSpriteDescription(look);

      return parsedLook.clothes.map((clothing) => {
        const part = normalizeAttribute(clothing.part);

        // Assume loadResource is an asynchronous function that returns a promise
        return loadResource(clothing.type, `./public/figures/${part}/`);
      });
    });

    // Use Promise.all to handle all resource loading
    return Promise.all(resourcePromises)
      .then((resources) => {
        // Handle the loaded resources if needed
        return resources; // Returning the resources if needed
      })
      .catch((error) => {
        // Handle any error during resource loading
        console.error("Error loading resources:", error);
        throw error; // Re-throwing to ensure proper error handling
      });
  }

  public async addNPCS(npcs: any[]): Promise<void> {
    await this.loadNPCResources();

    npcs.forEach((npc) => {
      this.npcManager.addNPC({
        id: npc.id,
        type: npc.type,
        position: vec(npc.start_posiiton.x, npc.start_posiiton.y),
      });
    });
  }

  public addPlayers(players: any[]): void {
    players.forEach(async (player) => {
      if (this.figureManager.getFigureById(player.id)) return;

      // Wait for assets to finish loading
      const playerFigure = this.figureManager.addFigure({
        id: player.id,
        username: player.username,
        look: player.look,
        direction: player.direction,
        position: vec(player.position?.x, player.position?.y),
        tilemapPosition: this.roomTileMap.tileMap.pos,
      });

      if (playerFigure) {
        // if (player.id === GlobalPartySocket.ws.id) {
        this.setCameraPositionToPlayer(playerFigure.figure);
        // }

        this.add(playerFigure.figure);
      }
    });
  }

  public removePlayer(id: string) {
    const playerFigure = this.figureManager.getFigureById(id) as any;
    if (!playerFigure) return;

    // Remove the player's figure from the tile map, marking the tile as not solid (set it to 0)
    this.roomTileMap.removeSolidTile(playerFigure.position);
    this.engine.remove(playerFigure.figure);
    this.figureManager.removeFigureById(id);
  }

  async loadFurnitureResources(furnitures) {
    console.groupCollapsed("Loaded resources (" + furnitures.length + ")");
    const resourcePromises = furnitures.map((furniture) => {
      console.log(
        `%cLoaded asset: %c${furniture.slug}`,
        "color: yellow; font-weight: bold;",
        "color: orange;"
      );

      return loadResource(furniture.slug);
    });
    console.groupEnd();

    return Promise.all(resourcePromises)
      .then((resources) => {
        return resources; // Returning the resources if needed
      })
      .catch((error) => {
        console.error("Error loading resources:", error);
        throw error;
      });
  }

  async loadNPCResources() {
    await loadResource("dog", "./public/npc/");
  }

  public async addFurnituresToRoom(furnitures) {
    // Load resources
    await this.loadFurnitureResources(furnitures);

    furnitures.map((furniture) => {
      if (!loadedAssets[furniture.slug].isLoaded()) return;
      const locationFurniture = this.furnitureManager.add(furniture);

      this.engine.add(locationFurniture);
    });
  }

  public setEventListners() {
    this.furniViewer.addEventListener("furni-move", (event: CustomEvent) => {
      gameApp.wsClient.sendMessage(
        JSON.stringify({
          action: "furniture_actions",
          arguments: {
            action: "move",
            id: event.detail.id,
          },
        })
      );
    });

    this.furniViewer.addEventListener("furni-rotate", (event: CustomEvent) => {
      gameApp.wsClient.sendMessage(
        JSON.stringify({
          action: "furniture_actions",
          arguments: {
            action: "rotate",
            id: event.detail.id,
          },
        })
      );
    });

    this.furniViewer.addEventListener("furni-delete", (event: CustomEvent) => {
      gameApp.wsClient.sendMessage(
        JSON.stringify({
          action: "furniture_actions",
          arguments: {
            action: "pickup",
            id: event.detail.id,
          },
        })
      );
    });

    window.addEventListener(
      "buildmode-furniture-selected",
      (event: CustomEvent) => {
        this.furnitureManager.createFakeFurniture({
          x: event.detail.x,
          y: event.detail.y,
          slug: event.detail.slug,
        });
      }
    );

    window.addEventListener(
      "buildmode-furniture-unselect",
      (event: CustomEvent) => {
        this.furnitureManager.removeFakeFurniture();
      }
    );

    window.addEventListener("furni-dropped", (event: CustomEvent) => {
      const buildMode = document.querySelector("build-mode") as any;
      buildMode?.collapse();

      gameApp.wsClient.sendMessage(
        JSON.stringify({
          action: "furniture_actions",
          arguments: {
            action: "drop",
            id: event.detail.id,
            slug: event.detail.slug,
            isNew: event.detail.isNew,
            target: {
              x: event.detail.snappedTile.x,
              y: event.detail.snappedTile.y,
            },
          },
        })
      );
    });

    window.addEventListener("coin-update", () => {});

    this.on(
      "move-user",
      ({
        id,
        path,
        endAction,
      }: {
        id: string;
        path: any[];
        endAction: string;
      }) => {
        const player = this.figureManager.getFigureById(id) as any;
        player?.figure.movePlayer(path, "sit");
      }
    );
  }

  public displayGameUI() {
    // Close model when room is loaded
    const navigatorModal = document.querySelector("navigator-screen") as any;
    navigatorModal?.closeModal();

    this.furniViewer = new FurniViewer();
    this.userViewer = new UserViewer();

    this.chatbox = new Chatbox();
    this.locationInfo = new LocationInfo();

    const buildMode = new BuildMode();

    const chatBox = document.createElement("chat-box");
    document.getElementById("chat")?.appendChild(chatBox);

    render(
      [this.furniViewer, this.userViewer, buildMode, this.locationInfo],
      document.body
    );
  }
}
